import { ArrowRight, BarChart3, BookOpenCheck, BriefcaseBusiness, FileText, FolderGit2, Loader2, Sparkles, Target } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { getStudentGrowthAssets, getStudentGrowthWeeklyReport } from '../api.js'
import { useAuth } from '../auth-context.js'
import { setPageMetadata } from '../page-metadata.js'

function ScoreCard({ label, value }) {
  return (
    <article>
      <span>{label}</span>
      <strong>{Number(value || 0)}</strong>
      <i><b style={{ width: `${Math.min(100, Number(value || 0))}%` }} /></i>
    </article>
  )
}

function ListPanel({ icon: Icon, title, items, render }) {
  return (
    <section className="growth-assets-panel">
      <header><Icon size={18} /><h2>{title}</h2></header>
      <div className="growth-assets-list">
        {items?.length ? items.slice(0, 8).map((item, index) => (
          <article key={`${title}-${index}`}>{render ? render(item) : <span>{String(item)}</span>}</article>
        )) : <p>暂无数据，完成学习、练习或项目后会自动沉淀。</p>}
      </div>
    </section>
  )
}

export default function StudentGrowthAssetsPage() {
  const auth = useAuth()
  const [state, setState] = useState({ loading: true, assets: null, report: null, error: '' })

  useEffect(() => {
    setPageMetadata({
      title: '成长资产库 - 青途 AI',
      description: '汇总技能、项目、学习记录、求职材料、成长评分和AI成长导师建议。',
    })
  }, [])

  useEffect(() => {
    if (auth?.loading || !auth?.user) {
      setState({ loading: false, assets: null, report: null, error: '' })
      return undefined
    }
    const controller = new AbortController()
    setState((current) => ({ ...current, loading: true, error: '' }))
    Promise.all([
      getStudentGrowthAssets(controller.signal),
      getStudentGrowthWeeklyReport(controller.signal),
    ])
      .then(([assets, report]) => setState({ loading: false, assets, report, error: '' }))
      .catch((error) => {
        if (error?.name === 'AbortError') return
        setState({ loading: false, assets: null, report: null, error: error?.message || '成长资产加载失败' })
      })
    return () => controller.abort()
  }, [auth?.loading, auth?.user])

  if (auth?.loading || state.loading) {
    return (
      <main className="page-container growth-assets-page">
        <section className="growth-assets-hero"><span><Loader2 size={18} /> 正在加载成长资产...</span></section>
      </main>
    )
  }

  if (!auth?.user) {
    return (
      <main className="page-container growth-assets-page">
        <section className="growth-assets-hero">
          <span><Sparkles size={16} /> 成长资产库</span>
          <h1>登录后查看你的长期成长资产</h1>
          <p>系统会汇总学习、练习、项目、简历和面试准备，生成成长评分和下一步建议。</p>
          <Link className="primary-button" to="/login">去登录 <ArrowRight size={16} /></Link>
        </section>
      </main>
    )
  }

  const assets = state.assets?.assets || {}
  const score = state.assets?.score || {}
  const mentor = state.assets?.mentor || {}
  const report = state.report || {}

  return (
    <main className="page-container growth-assets-page">
      <section className="growth-assets-hero">
        <div>
          <span><Sparkles size={16} /> 成长资产库 + AI成长导师 MVP</span>
          <h1>你的成长指数：{Number(score.growthIndex || 0)}</h1>
          <p>{mentor.reason || '完成学习、练习、项目和求职准备后，系统会持续理解你的长期成长。'}</p>
        </div>
        <Link className="secondary-button" to="/learning">继续学习路线</Link>
      </section>

      {state.error ? <p className="student-growth-error">{state.error}</p> : null}

      <section className="growth-assets-score-grid">
        <ScoreCard label="技能能力" value={score.skillAbility} />
        <ScoreCard label="项目经验" value={score.projectExperience} />
        <ScoreCard label="学习完成度" value={score.learningCompletion} />
        <ScoreCard label="求职准备度" value={score.careerReadiness} />
      </section>

      <section className="growth-assets-mentor">
        <header><Target size={18} /><h2>AI成长导师建议</h2></header>
        <strong>{mentor.focusArea || '下一步建议'}</strong>
        {(mentor.nextStepSuggestions || []).map((item) => <p key={item}>{item}</p>)}
      </section>

      <div className="growth-assets-grid">
        <ListPanel icon={BookOpenCheck} title="已掌握技能" items={assets.masteredSkills} />
        <ListPanel
          icon={FolderGit2}
          title="完成项目"
          items={assets.completedProjects}
          render={(item) => (
            <>
              <strong>{item.title}</strong>
              <span>{item.summary || item.status} · {item.progress || 0}%</span>
            </>
          )}
        />
        <ListPanel
          icon={BarChart3}
          title="学习记录"
          items={assets.learningRecords}
          render={(item) => (
            <>
              <strong>{item.title}</strong>
              <span>{item.type} · {item.status || item.completedAt || '已记录'}</span>
            </>
          )}
        />
        <ListPanel
          icon={BriefcaseBusiness}
          title="求职材料"
          items={assets.careerMaterials}
          render={(item) => (
            <>
              <strong>{item.title}</strong>
              <span>{item.type}{item.score ? ` · ${item.score}分` : ''}</span>
            </>
          )}
        />
      </div>

      <section className="growth-assets-report">
        <header><FileText size={18} /><h2>周总结</h2><span>{report.weekStart} - {report.weekEnd}</span></header>
        <div>
          <article><strong>完成内容</strong>{(report.completedContent || []).map((item) => <p key={item}>{item}</p>)}</article>
          <article><strong>新增能力</strong>{(report.newAbilities || []).map((item) => <p key={item}>{item}</p>)}</article>
          <article><strong>薄弱点</strong>{(report.weakPoints || []).map((item) => <p key={item}>{item}</p>)}</article>
          <article><strong>下一步建议</strong>{(report.nextSuggestions || []).map((item) => <p key={item}>{item}</p>)}</article>
        </div>
      </section>
    </main>
  )
}
